import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {plotGetAll, plotExcel} from '../../../redux/slices/project.slice'

import {pagination} from '../../../helpers'

import {
  useMemoizedStaticData
} from '../../../utils/performanceOptimizations'

const selectPlotData = (state) => ({
  plot: state.project.plot,
  loading: state.project.loading
})

function List() {
  const intl = useIntl()
  const {plot, loading} = useSelector(selectPlotData)
  const dispatch = useDispatch()

  const [params, setParams] = useState(pagination)

  useEffect(() => {
    dispatch(plotGetAll(params))
  }, [dispatch, params])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      plotExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.PLOT'}),
      })
    )
  }, [dispatch, params, intl])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PLOT.SEARCH.NAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'name',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PLOT.SEARCH.PROJECTNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PLOT.SEARCH.CONSTRUCTIONSITENAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'constructionSiteName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PLOT.SEARCH.BLOCKNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'blockName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PLOT.SEARCH.BUILDINGBLOCKTYPENAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'buildingBlockTypeName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PLOT.SEARCH.BASEMENTHEIGHT'})}
          className='min-w-125px'
        />
      ),
      accessor: 'basementHeight',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PLOT.SEARCH.DESCRIPTION'})}
          className='min-w-125px'
        />
      ),
      accessor: 'description',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
  ], [intl, handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
  ], [handleExcelExport])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.PLOT'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
        />
        <KTCardBody>
          {!plot && <ListLoading />}
          {plot && (
            <Table
              items={plot}
              cols={columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
